/**
 * App Users Module Helper Functions
 */
import { t } from 'i18next'

/**
 * Get user full name from user object
 * @param {Object} user - User object
 * @returns {string} Full name or email as fallback
 */
export const getUserFullName = (user) => {
    const firstName = user?.user_info?.given_name || ''
    const lastName = user?.user_info?.family_name || ''
    const fullName = `${firstName} ${lastName}`.trim()
    return fullName || user?.email || ''
}

/**
 * Get trip status badge properties
 * @param {string} tripStatus - Trip status code
 * @returns {Object} Badge color and text
 */
export const getTripStatusBadge = (tripStatus) => {
    const statusMap = {
        'CXL': { color: 'red', text: t('Cancelled') },
        'ACT': { color: 'emerald', text: t('Active') },
        'COM': { color: 'blue', text: t('Completed') },
        'PEN': { color: 'yellow', text: t('Pending') },
        'CNF': { color: 'emerald', text: t('Confirmed') },
        'CHK': { color: 'purple', text: t('Checked-in') },
    }
    return statusMap[tripStatus] || { color: 'zinc', text: '' }
}

/**
 * Check if booking is Day Pass type
 * @param {Object} booking - Booking object
 * @returns {boolean}
 */
export const isDayPassBooking = (booking) => {
    return booking?.booking_type?.code === 'DYP'
}

/**
 * Get travel date for a booking
 * @param {Object} booking - Booking object
 * @returns {string} Travel date
 */
export const getBookingTravelDate = (booking) => {
    if (isDayPassBooking(booking)) {
        return booking?.passengers?.[0]?.travel_date
    }
    return booking?.trip_segments?.[0]?.travel_date
}

/**
 * Get passengers for a booking
 * @param {Object} booking - Booking object
 * @returns {Array} Passengers array
 */
export const getBookingPassengers = (booking) => {
    if (isDayPassBooking(booking)) {
        return booking?.passengers || []
    }
    return booking?.trip_segments?.[0]?.passengers || []
}

/**
 * Get passenger count for a booking
 * @param {Object} booking - Booking object
 * @returns {number} Passenger count
 */
export const getPassengerCount = (booking) => {
    return getBookingPassengers(booking).length
}

/**
 * Get first passenger info from booking
 * @param {Object} booking - Booking object
 * @returns {Object|null} First passenger or null
 */
export const getFirstPassenger = (booking) => {
    const passengers = getBookingPassengers(booking)
    return passengers[0]?.passenger || null
}

/**
 * Get user status badge color
 * @param {boolean} isActive - User active status
 * @returns {string} Badge color
 */
export const getUserStatusBadgeColor = (isActive) => {
    return isActive ? 'emerald' : 'rose'
}

/**
 * Get user status text
 * @param {boolean} isActive - User active status
 * @returns {string} Status text
 */
export const getUserStatusText = (isActive) => {
    return isActive ? t('Active') : t('Inactive')
}

/**
 * Get confirmation message for enable/disable action
 * @param {boolean} enableAction - True if enabling, false if disabling
 * @returns {string} Confirmation message
 */
export const getStatusConfirmMessage = (enableAction) => {
    const action = enableAction ? t('enable') : t('disable')
    return t('Are you sure you want to {{action}} this user?', { action })
}
